import { useMemo, useState, type ComponentType } from 'react';

import { Button } from '@/components/ui';
import { cn } from '@/lib/cn';

/**
 * KanbanCalendarView — vista calendário mensal (R5.1 Bloco D / FEAT-V2-006).
 *
 * Custom HTML/CSS Grid (~150 LOC), sem dep externa (decisão #4 do
 * status-r51r52-progresso.md). Suficiente pra vista mensal — week view +
 * time scale + recurring fica como TD pra futuro.
 *
 * Genérico sobre `T` — recebe items + `getItemDate(item)` que extrai data
 * agendada (ISO string ou null). Items sem data ficam no balde "Sem data".
 *
 * Drag entre dias = reagendar OS via callback. Sem drag inicial nesta
 * rodada (TD); só visualização + click no item abre detail (via callback).
 */

const MES_LABELS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
const DIA_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export interface KanbanCalendarItemProps<T> {
  item: T;
  /** Click no item — caller decide o que fazer (ex: abrir detail dialog). */
  onClick: () => void;
}

export interface KanbanCalendarViewProps<T extends { id: string }> {
  items: T[];
  /** Extrai data agendada do item — null/undefined = item vai pro balde "Sem data". */
  getItemDate: (item: T) => string | null | undefined;
  /** Componente que renderiza um item dentro de uma célula de dia. Mini-card. */
  itemComponent: ComponentType<KanbanCalendarItemProps<T>>;
  /** Click em qualquer item — recebe o item completo. */
  onItemClick?: (item: T) => void;
  /** Data inicial do mês a exibir. Default: hoje. */
  initialMonth?: Date;
}

/** Retorna primeiro dia do mês (date-fns-free). */
function firstOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/** Compara só YYYY-MM-DD. */
function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Gera array de 6 weeks × 7 days = 42 datas, preenchendo do domingo da
 * primeira semana do mês até o sábado da última (padrão calendário PT-BR).
 */
function buildMonthGrid(monthStart: Date): Date[] {
  const start = new Date(monthStart);
  start.setDate(start.getDate() - start.getDay()); // recua até domingo
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

export function KanbanCalendarView<T extends { id: string }>(props: KanbanCalendarViewProps<T>) {
  const { items, getItemDate, itemComponent: ItemComponent, onItemClick, initialMonth } = props;

  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState<Date>(firstOfMonth(initialMonth ?? today));

  const days = useMemo(() => buildMonthGrid(cursor), [cursor]);

  // Map YYYY-MM-DD → items[]
  const itemsByDay = useMemo(() => {
    const m = new Map<string, T[]>();
    const semData: T[] = [];
    for (const item of items) {
      const iso = getItemDate(item);
      if (!iso) {
        semData.push(item);
        continue;
      }
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) {
        semData.push(item);
        continue;
      }
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const arr = m.get(key) ?? [];
      arr.push(item);
      m.set(key, arr);
    }
    return { byKey: m, semData };
  }, [items, getItemDate]);

  function prevMonth() {
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  }
  function nextMonth() {
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  }
  function goToToday() {
    setCursor(firstOfMonth(today));
  }

  const currentMonth = cursor.getMonth();

  return (
    <div data-testid="kanban-calendar-view">
      {/* Header com navegação */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-xl text-ink-1 dark:text-ink-dark-1">
          {MES_LABELS[currentMonth]} {cursor.getFullYear()}
        </h3>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="calendar-prev"
            onClick={prevMonth}
            aria-label="Mês anterior"
          >
            ◀
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="calendar-today"
            onClick={goToToday}
          >
            Hoje
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="calendar-next"
            onClick={nextMonth}
            aria-label="Próximo mês"
          >
            ▶
          </Button>
        </div>
      </div>

      {/* Grid de dias da semana */}
      <div className="grid grid-cols-7 gap-px bg-line dark:bg-line-dark rounded-xl overflow-hidden border border-line dark:border-line-dark">
        {DIA_LABELS.map((d) => (
          <div
            key={d}
            className="text-xs uppercase tracking-wider text-ink-3 dark:text-ink-dark-3 text-center py-1.5 bg-paper-warm dark:bg-paper-dark-warm"
          >
            {d}
          </div>
        ))}
        {days.map((d) => {
          const inCurrentMonth = d.getMonth() === currentMonth;
          const isToday = sameDay(d, today);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          const dayItems = itemsByDay.byKey.get(key) ?? [];
          return (
            <div
              key={key}
              data-testid={`calendar-day-${key}`}
              data-in-month={inCurrentMonth}
              className={cn(
                'min-h-[80px] p-1 flex flex-col gap-0.5',
                inCurrentMonth
                  ? 'bg-paper-surface dark:bg-paper-dark-surface'
                  : 'bg-paper dark:bg-paper-dark opacity-50',
                isToday && 'ring-2 ring-red ring-inset'
              )}
            >
              <span
                className={cn(
                  'text-xs tabular-nums',
                  isToday
                    ? 'font-bold text-red'
                    : 'text-ink-3 dark:text-ink-dark-3'
                )}
              >
                {d.getDate()}
              </span>
              {dayItems.slice(0, 3).map((item) => (
                <ItemComponent
                  key={item.id}
                  item={item}
                  onClick={() => onItemClick?.(item)}
                />
              ))}
              {dayItems.length > 3 && (
                <span
                  data-testid={`calendar-day-${key}-overflow`}
                  className="text-xs text-ink-3 dark:text-ink-dark-3 italic px-1"
                >
                  + {dayItems.length - 3} mais
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Items sem data agendada */}
      {itemsByDay.semData.length > 0 && (
        <div
          data-testid="calendar-no-date-bucket"
          className="mt-4 p-3 rounded-xl border border-dashed border-line dark:border-line-dark"
        >
          <p className="text-xs text-ink-3 dark:text-ink-dark-3 mb-2">
            Sem data agendada ({itemsByDay.semData.length})
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {itemsByDay.semData.map((item) => (
              <ItemComponent
                key={item.id}
                item={item}
                onClick={() => onItemClick?.(item)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
